import { ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { isGuestAllowedRoute, isPublicRoute } from '@/constant/auth-public-routes';
import { useGuestSessionStore } from './store/guestSession';

/**
 * useAuthState Hook
 *
 * A custom hook that provides authentication state with mounting status tracking.
 * It synchronizes with the global auth store and ensures components can detect
 * when auth state is fully loaded.
 *
 * @returns {AuthState} An object containing authentication status information
 *   - isMounted: Whether the auth state has been initialized
 *   - isAuthenticated: Whether the user is authenticated
 *
 * @example
 * const { isMounted, isAuthenticated } = useAuthState();
 *
 * if (!isMounted) {
 *   return <LoadingSpinner />;
 * }
 *
 * return isAuthenticated ? <AuthenticatedApp /> : <UnauthenticatedApp />;
 */

interface AuthState {
  isMounted: boolean;
  isAuthenticated: boolean;
}

export const useAuthState = () => {
  const { isAuthenticated } = useAuthStore();
  const [isAuth, setIsAuth] = useState<AuthState>({
    isMounted: false,
    isAuthenticated: false,
  });

  useEffect(() => {
    setIsAuth({
      isMounted: true,
      isAuthenticated: isAuthenticated,
    });
  }, [isAuthenticated]);

  return isAuth;
};

/**
 * ClientMiddleware Component
 *
 * A protection layer that controls rendering based on authentication state,
 * automatically redirecting unauthenticated users away from protected routes.
 *
 * Features:
 * - Blocks access to protected routes for unauthenticated users
 * - Redirects to login page when authentication is required
 * - Allows access to public routes without authentication
 * - Prevents content flash by not rendering until auth state is confirmed
 *
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components to render when access is granted
 * @returns {JSX.Element|null} The children when access is granted, null otherwise
 *
 * @example
 * <ClientMiddleware>
 *   <ProtectedRoutes />
 * </ClientMiddleware>
 */

interface ClientMiddlewareProps {
  children: ReactNode;
}

export const ClientMiddleware: React.FC<ClientMiddlewareProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { isMounted, isAuthenticated } = useAuthState();
  const isPublic = isPublicRoute(currentPath);
  const isGuestRoute = isGuestAllowedRoute(currentPath);
  const ensureGuestSession = useGuestSessionStore((s) => s.ensureGuestSession);
  const guestBlocksToken = useGuestSessionStore((s) => s.guestBlocksToken);

  // Check if we're processing an SSO callback (has code and state parameters)
  const urlParams = new URLSearchParams(location.search);
  const isSSOCallback = !!(urlParams.get('code') && urlParams.get('state'));

  // Lazily provision a guest session when an unauthenticated visitor lands
  // on a guest-allowed route. This is the only place we mint guest sessions
  // — call sites elsewhere should NOT call ensureGuestSession() preemptively.
  useEffect(() => {
    if (!isMounted) return;
    if (isAuthenticated) return;
    if (!isGuestRoute) return;
    ensureGuestSession().catch((err) => {
      console.error('[guest-session] ensureGuestSession failed', err);
    });
  }, [isMounted, isAuthenticated, isGuestRoute, ensureGuestSession]);

  useLayoutEffect(() => {
    if (isSSOCallback) {
      return;
    }

    if (isMounted && !isAuthenticated && !isPublic && !isGuestRoute) {
      navigate('/login');
    }
  }, [
    isAuthenticated,
    isMounted,
    isPublic,
    isGuestRoute,
    isSSOCallback,
    navigate,
    currentPath,
  ]);

  // Don't block rendering if we're processing SSO callback
  if (isSSOCallback) {
    return <>{children}</>;
  }

  if ((!isMounted || !isAuthenticated) && !isPublic && !isGuestRoute) return null;

  // Guest gate: on a guest-allowed route, do not render children until the
  // guest Blocks token is in the store. Otherwise the page's data hooks
  // fire UDS/gateway calls before any Bearer is set, the gateway returns
  // null-fields rows, and the UI shows "Template Not Found".
  if (
    !isAuthenticated &&
    isGuestRoute &&
    !guestBlocksToken
  ) {
    return null;
  }

  return <>{children}</>;
};
